import {
  OP_OR,
  OP_AND,
  OP_ALWAYS,
  OP_EQUALS,
  OP_NOT_EQUALS,
  TASK_TYPES,
  TYPE_OPTION,
  TYPE_QUESTION,
  LOGIC_DETAILS_TYPE_TASK,
  LOGIC_DETAILS_TYPE_NEXT_TASK,
  LOGIC_DETAILS_TYPE_END_OF_TEST,
  LOGIC_DETAILS_TYPE_END_TEST_WITH_DISQUALIFICATION,
  TASK_STATUS,
} from "../actions/constants";
import { canHaveLogic } from "./common";
import { convertTokensToExpressions, treeToTokens } from "./treeUtils";

export const flattenTasks = (tasksArr, removeDeletedTasks = true) => {
  let index = 0;
  const flattenTasksArr = [];
  tasksArr.forEach((task) => {
    if ((removeDeletedTasks && Number(task?.status) !== TASK_STATUS.DELETED) || !removeDeletedTasks) {
      if (task?.subTasks && task.subTasks.length > 0) {
        task.subTasks.forEach((subTask) => {
          if ((removeDeletedTasks && Number(subTask?.status) !== TASK_STATUS.DELETED) || !removeDeletedTasks) {
            flattenTasksArr.push({ ...subTask, flattenPosition: index++ });
          }
        });
      } else flattenTasksArr.push({ ...task, flattenPosition: index++ });
    }
  });
  return flattenTasksArr;
};

export const getObservedAndUpcomingTasks = (tasksArr, taskId, isGroup = false) => {
  const showScreener = JSON.parse(localStorage.getItem("showScreener") || "false");

  const activeTaskIndex = tasksArr.findIndex(({ id }) => id === taskId);
  const allTasks = JSON.parse(JSON.stringify(tasksArr));
  const tillTasks = flattenTasks(allTasks.splice(0, isGroup ? activeTaskIndex : activeTaskIndex + 1));
  const observedLogicTasks = tillTasks.filter((task) => {
    return (
      canHaveLogic(task, showScreener) &&
      task.options.length > 0 &&
      (!showScreener ? String(task.options[0]?.name ?? "").trim() !== "" : String(task.options[0]?.title ?? "").trim() !== "")
    );
  });

  if (isGroup) allTasks.shift();
  const upcomingTasks = flattenTasks(JSON.parse(JSON.stringify(allTasks)));
  return { observedLogicTasks, upcomingTasks };
};

export const getOperatorLabel = (op) => {
  switch (op) {
    case OP_OR:
      return "Or";
    case OP_AND:
      return "And";
    case OP_EQUALS:
      return "is";
    case OP_NOT_EQUALS:
      return "is not";
    case OP_ALWAYS:
      return "always";
    default:
      return "";
  }
};

const verifyLogicIntegrity = (task, observedLogicTasks, upcomingTasks) => {
  const showScreener = JSON.parse(localStorage.getItem("showScreener") || "false");

  let generalErrors = [];
  let logicErrors = {};

  const logics = task.logic?.logic;
  logics?.forEach((logic, logicIndex) => {
    // If Statement (checking Condition block)
    const expressions = convertTokensToExpressions(treeToTokens(logic?.condition))?.expression;
    expressions?.forEach((expression, expressionIndex) => {
      const question = observedLogicTasks?.find((task) => task?.id === expression?.question);
      if (!question) {
        // question should be in observedLogicTasks
        if (!logicErrors[logicIndex]) logicErrors[logicIndex] = {};
        if (!logicErrors[logicIndex]["condition"]) logicErrors[logicIndex]["condition"] = {};
        if (!logicErrors[logicIndex]["condition"][expressionIndex]) logicErrors[logicIndex]["condition"][expressionIndex] = [];
        logicErrors[logicIndex]["condition"][expressionIndex].push(
          `Condition in task ${task.id} referes to an unobserved task ${expression?.question}`
        );
      }

      const option = question?.options?.find((option) => {
        const name = !showScreener ? option?.name : option?.title;
        return name === expression?.option;
      });
      if (!option) {
        // option should be an valid option of question
        if (!logicErrors[logicIndex]) logicErrors[logicIndex] = {};
        if (!logicErrors[logicIndex]["condition"]) logicErrors[logicIndex]["condition"] = {};
        if (!logicErrors[logicIndex]["condition"][expressionIndex]) logicErrors[logicIndex]["condition"][expressionIndex] = [];
        logicErrors[logicIndex]["condition"][expressionIndex].push(
          `Condition in task ${task.id} referes to an invalid option ${expression?.option} of question ${expression?.question}`
        );
      }
    });

    // Then Statement (checking Action block)
    const details = logic?.details?.to;
    if (details?.type === LOGIC_DETAILS_TYPE_TASK) {
      const goToTask = upcomingTasks?.find((task) => task?.id === details?.value);
      if (!goToTask) {
        // if type = task, then value should be in upcomingTasks
        if (!logicErrors[logicIndex]) logicErrors[logicIndex] = {};
        if (!logicErrors[logicIndex]["action"]) logicErrors[logicIndex]["action"] = [];
        logicErrors[logicIndex]["action"].push(`Action in task ${task.id} leads to a task ${details?.value} that isn't upcoming`);
      }
    } else if (
      details?.type === LOGIC_DETAILS_TYPE_NEXT_TASK ||
      details?.type === LOGIC_DETAILS_TYPE_END_OF_TEST ||
      details?.type === LOGIC_DETAILS_TYPE_END_TEST_WITH_DISQUALIFICATION
    ) {
      if (details?.value !== -1) {
        // if type = next_task or end_of_test, then value should be -1
        if (!logicErrors[logicIndex]) logicErrors[logicIndex] = {};
        if (!logicErrors[logicIndex]["action"]) logicErrors[logicIndex]["action"] = [];
        logicErrors[logicIndex]["action"].push(`Action in task ${task.id} should have value -1 because type is ${details?.type}`);
      }
    }
  });

  return { generalErrors, logicErrors, isValid: Object.keys(logicErrors).length === 0 && generalErrors.length === 0 };
};

export const validateLogics = (tasks) => {
  const logicValidationMap = {};
  tasks.forEach((task) => {
    if (task.type === TASK_TYPES.GROUP) {
      // Group Task
      const groupId = task.id;
      const subTasks = task.subTasks;
      subTasks?.forEach((subTask) => {
        const taskId = subTask.id;
        if (subTask.logic) {
          const { observedLogicTasks, upcomingTasks } = getObservedAndUpcomingTasks(tasks, groupId, true);
          const { observedLogicTasks: observedLogicTasksInsideGroup, upcomingTasks: upcomingTasksInsideGroup } =
            getObservedAndUpcomingTasks(subTasks, taskId);
          const allObservedLogicTasks = observedLogicTasks.concat(observedLogicTasksInsideGroup);
          const allUpcomingTasks = upcomingTasksInsideGroup.concat(upcomingTasks);
          logicValidationMap[taskId] = verifyLogicIntegrity(subTask, allObservedLogicTasks, allUpcomingTasks);
        }
      });
    } else {
      // Root level task
      const taskId = task.id;
      if (task.logic) {
        const { observedLogicTasks, upcomingTasks } = getObservedAndUpcomingTasks(tasks, taskId);
        logicValidationMap[taskId] = verifyLogicIntegrity(task, observedLogicTasks, upcomingTasks);
      }
    }
  });
  return logicValidationMap;
};

export const removeBrokenLogics = (logicValidationMap, tasks) => {
  let updatedTasks = JSON.parse(JSON.stringify(tasks));
  const brokenLogics = Object.entries(logicValidationMap)?.filter((item) => item[1]?.isValid === false);
  brokenLogics?.forEach((item) => {
    const taskId = item[0];
    const details = item[1];
    const generalErrors = details?.generalErrors;
    const logicErrors = details?.logicErrors;

    updatedTasks.forEach((task) => {
      if (taskId === task.id) {
        if (generalErrors && generalErrors.length) {
          delete task.logic;
        } else if (logicErrors) {
          const logicErrorsArr = Object.entries(logicErrors);
          for (let i = logicErrorsArr.length - 1; i >= 0; i--) {
            const errorLogicIndex = logicErrorsArr[i][0];
            const problematicLogic = task.logic.logic[errorLogicIndex];
            if (problematicLogic?.condition?.op === OP_ALWAYS) {
              problematicLogic.details.to = { type: LOGIC_DETAILS_TYPE_NEXT_TASK, value: -1 };
            } else {
              task.logic.logic.splice(errorLogicIndex, 1);
            }
          }
        }
      } else if (task.subTasks && task.subTasks.length) {
        task.subTasks.forEach((subTask) => {
          if (taskId === subTask.id) {
            if (generalErrors && generalErrors.length) {
              delete subTask.logic;
            } else if (logicErrors) {
              const logicErrorsArr = Object.entries(logicErrors);
              for (let i = logicErrorsArr.length - 1; i >= 0; i--) {
                const errorLogicIndex = logicErrorsArr[i][0];
                const problematicLogic = subTask.logic.logic[errorLogicIndex];
                if (problematicLogic?.condition?.op === OP_ALWAYS) {
                  problematicLogic.details.to = { type: LOGIC_DETAILS_TYPE_NEXT_TASK, value: -1 };
                } else {
                  subTask.logic.logic.splice(errorLogicIndex, 1);
                }
              }
            }
          }
        });
      }
    });
  });
  return updatedTasks;
};

const evaluateVar = (givenAnswers, variable) => {
  if (variable.type === TYPE_QUESTION) {
    return givenAnswers[variable.value];
  } else if (variable.type === TYPE_OPTION) {
    return variable.value;
  }
};

const evaluateCondition = (givenAnswers, condition) => {
  const { op, vars } = condition;

  if (op === OP_ALWAYS) {
    return true;
  }

  if (op === OP_EQUALS) {
    const [var1, var2] = vars;
    const result1 = evaluateVar(givenAnswers, var1);
    const result2 = evaluateVar(givenAnswers, var2);
    return typeof result1 === "object" ? result1.includes(result2) : result1 === result2;
  }

  if (op === OP_NOT_EQUALS) {
    const [var1, var2] = vars;
    const result1 = evaluateVar(givenAnswers, var1);
    const result2 = evaluateVar(givenAnswers, var2);
    return typeof result1 === "object" ? !result1.includes(result2) : result1 !== result2;
  }

  if (op === OP_AND) {
    return vars.every((v) => evaluateCondition(givenAnswers, v));
  }

  if (op === OP_OR) {
    return vars.some((v) => evaluateCondition(givenAnswers, v));
  }

  return false; // Default case when the operator is not recognized
};

export const findNextTaskId = (givenAnswers, logics) => {
  for (const logic of logics) {
    const condition = logic.condition;
    if (evaluateCondition(givenAnswers, condition)) {
      return logic.details.to;
    }
  }

  return null; // Default case when no conditions are met
};
